// Global CuoreCare real-time provider for the doctor portal.
//
// Responsibilities:
//   - Hold ONE socket.io connection for the whole app (survives route changes because it's
//     mounted above <Routes>), authenticated with the doctor JWT.
//   - Surface an incoming-call modal ("Patient is waiting") the instant a patient joins a
//     consultation before the doctor — from any page, with a ringtone.
//   - Provide a lightweight toast API + the raw socket (via context) so pages like the
//     Console can react to appointment:new / appointment:updated without polling.
//
// The connection is gated on the logged-in doctor being CuoreCare-enabled; a non-CuoreCare
// doctor (or logged-out user) simply gets no socket.
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EV from "./events";
import { startRing, stopRing, playNotify } from "./ringtone";
import IncomingCallModal from "../components/cuorecare/IncomingCallModal";
import { receiveLive } from "../redux/slices/notificationsSlice";

const API_URL = process.env.REACT_APP_API_URL || "https://cuore-backend-995g.onrender.com";
const RING_TIMEOUT_MS = 45000; // auto-dismiss the "patient waiting" ring if unanswered

const SocketCtx = createContext({ socket: null, connected: false, notify: () => {} });
export const useSocket = () => useContext(SocketCtx);

function readToken() {
  const raw = localStorage.getItem("token");
  return raw ? raw.replace(/^"|"$/g, "") : null;
}

export default function SocketProvider({ children }) {
  const navigate = useNavigate();
  const isCuoreCareDoctor = useSelector(
    (s) => s.profile?.data?.isCuoreCareDoctor,
  );

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const dispatch = useDispatch();
  const [incoming, setIncoming] = useState(null); // { appointmentId, mode, from, roomId }
  const [toasts, setToasts] = useState([]);
  const ringTimeoutRef = useRef(null);

  const notify = useCallback((message, tone = false) => {
    if (tone) playNotify();
    const id = `${Date.now()}_${Math.round(Math.random() * 1e6)}`;
    setToasts((t) => [...t, { id, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  const clearIncoming = useCallback(() => {
    stopRing();
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    setIncoming(null);
  }, []);

  // ---- establish / tear down the connection ----
  useEffect(() => {
    if (!isCuoreCareDoctor) return undefined;
    const token = readToken();
    if (!token) return undefined;

    const s = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
    });

    s.on("connect", () => setConnected(true));
    s.on("disconnect", () => setConnected(false));
    s.on("connect_error", (err) => {
      // Auth failures mirror the axios 401 handler: drop the session and bounce to login.
      if (String(err?.message || "").toLowerCase().includes("unauthorized")) {
        localStorage.removeItem("token");
        localStorage.removeItem("userData");
        if (!window.location.pathname.includes("/login")) window.location.href = "/login";
      }
    });

    setSocket(s);
    return () => {
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [isCuoreCareDoctor]);

  /* ---- global incoming-call handling ----
   *
   * THE SERVER NO LONGER RINGS A DOCTOR. This used to fire when a PATIENT joined first: the
   * backend rang whichever party was absent, so a patient arriving five minutes early raised
   * this full-screen modal, with sound, over a consultation the doctor was still conducting
   * with someone else. A doctor running late is by definition mid-consultation, so the ring
   * landed at exactly the worst moment.
   *
   * Opening a live consultation is now the doctor's move (see the backend's assertJoinable
   * DOCTOR_NOT_JOINED gate, and its join handler which rings the patient only). A waiting
   * patient reaches the doctor through PARTICIPANT_JOINED instead — the "Patient connected"
   * pill inside that consultation's own screen, which is presence rather than a summons.
   *
   * The listener is kept rather than deleted: the CALL_* stop-handlers below are still live,
   * and if a ringing path is ever reintroduced it should surface here rather than silently
   * nowhere. As of this change nothing emits CALL_INCOMING to a doctor. */
  useEffect(() => {
    if (!socket) return undefined;

    const onIncoming = (payload) => {
      if (!payload || !payload.appointmentId) return;
      setIncoming(payload);
      startRing();
      if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => clearIncoming(), RING_TIMEOUT_MS);
    };
    // Any of these means the ring should stop.
    const onStop = () => clearIncoming();

    socket.on(EV.CALL_INCOMING, onIncoming);
    socket.on(EV.CALL_ACCEPTED, onStop); // both parties now connected
    socket.on(EV.CALL_ENDED, onStop);
    socket.on(EV.CALL_MISSED, onStop);
    socket.on(EV.CALL_DECLINED, onStop);

    return () => {
      socket.off(EV.CALL_INCOMING, onIncoming);
      socket.off(EV.CALL_ACCEPTED, onStop);
      socket.off(EV.CALL_ENDED, onStop);
      socket.off(EV.CALL_MISSED, onStop);
      socket.off(EV.CALL_DECLINED, onStop);
    };
  }, [socket, clearIncoming]);

  /* ---- notification inbox: live delivery (spec D-series) ----
   * The console's ONLY live channel for a doctor notification — models/Doctor.js has no push
   * token, so there is no OS-level notification to fall back on. The durable copy is the
   * DoctorNotification row behind GET /api/web/notifications, which the bell fetches on mount;
   * this event exists so an open console updates without polling.
   *
   * Deliberately does NOT navigate or interrupt. A doctor mid-consultation must not be yanked
   * elsewhere by an unrelated alert — it updates the badge and plays the same soft cue the rest
   * of the console uses, and the doctor opens it when they choose.
   */
  useEffect(() => {
    if (!socket) return undefined;
    const onNotification = (payload) => {
      const note = payload && payload.notification;
      if (!note || !note._id) return;
      dispatch(receiveLive(note));
      // Best-effort audio cue; a blocked autoplay policy must not break the badge update.
      try {
        playNotify();
      } catch (_) {
        /* ignore */
      }
    };
    socket.on(EV.NOTIFICATION_NEW, onNotification);
    return () => socket.off(EV.NOTIFICATION_NEW, onNotification);
  }, [socket, dispatch]);

  // Stop the ring on unmount.
  useEffect(() => () => stopRing(), []);

  const onJoinCall = useCallback(() => {
    const id = incoming?.appointmentId;
    clearIncoming();
    if (id) navigate(`/cuorecare/consultation/${id}`);
  }, [incoming, clearIncoming, navigate]);

  /* Memoised, because this provider re-renders on a timer it does not control: every toast
   * pushes and then removes itself 5s later, and the incoming-call state changes on every ring.
   * A fresh object literal here would hand every consumer in the app a "new" context value on
   * each of those renders — including the consultation workspace, which is the one screen that
   * must not be disturbed mid-call. */
  const ctx = useMemo(() => ({ socket, connected, notify }), [socket, connected, notify]);

  return (
    <SocketCtx.Provider value={ctx}>
      {children}
      <IncomingCallModal call={incoming} onJoin={onJoinCall} onDismiss={clearIncoming} />
      <ToastStack toasts={toasts} />
    </SocketCtx.Provider>
  );
}

function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="rounded-xl bg-slate-900 text-white text-sm px-4 py-3 shadow-lg border border-slate-700/60 animate-[fadeIn_0.15s_ease-out]"
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}
